import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useState, type FC } from "react";
import type { UserContext } from "../../auth/auth";
import DatePicker from "../../components/datePicker/DatePickerV2";
import TagSelect from "../../components/tagSelect/TagSelect";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import { endOfNextYear, sanitizeDayjs, startOfPreviousMonth } from "../../misc/dateUtils";
import OrderStatusMultiSelect from "../orderStatus/OrderStatusMultiselect";
import { useTripTagsByCategory } from "../useTripTagsByCategory";
import ContactPersonSelect from "./ContactPersonSelect";
import "./OrdersSearch.scss";
import ShowHistoricalCheckbox from "./ShowHistoricalCheckbox";
import type { OrdersSearchCriteria } from "./useOrderSearch";

interface ActiveFilter {
  name: string;
  value: string;
  active: boolean;
  onDelete?: () => void;
}

interface ActiveFiltersProps {
  filters: ActiveFilter[];
}

const ActiveFilters: FC<ActiveFiltersProps> = ({ filters }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: 1,
        overflowX: "auto",
        width: "100%",
        pb: { xs: 1, md: 0 },
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {filters
        .filter((filter) => filter.active)
        .map((filter, index) => (
          <Chip
            key={index}
            label={`${filter.name}: ${filter.value}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ flexShrink: 0 }}
            onDelete={filter.onDelete}
          />
        ))}
    </Box>
  );
};

interface Props {
  readonly search: OrdersSearchCriteria;
  readonly context: UserContext | undefined;
  readonly showHistorical: boolean;
  readonly onChange: (search: Partial<OrdersSearchCriteria>) => void;
  readonly onShowHistoricalChange: (showHistorical: boolean) => void;
  readonly onClear: () => void;
  readonly onViewModeChange: (viewMode: "grid" | "list") => void;
  readonly viewMode: "grid" | "list";
}

const CustomerOrdersSearch: FC<Props> = ({
  search,
  context,
  showHistorical,
  onChange,
  onShowHistoricalChange,
  onClear,
  onViewModeChange,
  viewMode,
}) => {
  const { tripTagsByCategory } = useTripTagsByCategory();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const defaultEndDate = endOfNextYear();
  const filters: ActiveFilter[] = [
    { name: "Szukaj", value: search.text, active: search.text.trim() !== "", onDelete: () => onChange({ text: "" }) },
    {
      name: "Status",
      value: search.statuses.join(", "),
      active: search.statuses.length > 0,
      onDelete: () => onChange({ statuses: [] }),
    },
    {
      name: "Osoba kontaktowa",
      value: search.contactPersonEmail ?? "",
      active: !!search.contactPersonEmail,
      onDelete: () => onChange({ contactPersonEmail: null }),
    },
    {
      name: "Data rozpoczęcia od",
      value: search.startDate.format(ISO_DATE_FORMAT),
      active: search.startDate.isValid(),
      onDelete: () => onChange({ startDate: startOfPreviousMonth() }),
    },
    {
      name: "Data rozpoczęcia do",
      value: search.endDate.format(ISO_DATE_FORMAT),
      active: search.endDate.isValid() && !search.endDate.isSame(defaultEndDate),
      onDelete: () => onChange({ endDate: defaultEndDate }),
    },
    {
      name: "Tagi",
      value: search.tags.map((tag) => `${tag.category}: ${tag.value}`).join(", "),
      active: search.tags.length > 0,
      onDelete: () => onChange({ tags: [] }),
    },
    {
      name: "Historyczne",
      value: showHistorical ? "Tak" : "Nie",
      active: showHistorical,
      onDelete: () => onShowHistoricalChange(false),
    },
  ];
  return (
    <>
      {/* SEKCJA BARA AKCJI I FILTRÓW */}
      <Box sx={{ mb: 4 }}>
        {/* Pasek akcji */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
          }}
        >
          {/* Lewa strona - aktywne filtry */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 1, md: 1 },
              flex: 1,
              minWidth: 0,
              width: "100%",
              mr: { md: 2 },
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500, whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Aktywne filtry:
            </Typography>

            <ActiveFilters filters={filters} />
          </Box>

          {/* Prawa strona - przyciski i widoki */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "space-between", md: "flex-end" },
              width: { xs: "100%", md: "auto" },
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                variant={filtersOpen ? "contained" : "outlined"}
                color="primary"
                size="small"
                startIcon={<FilterListIcon fontSize="small" />}
                onClick={() => setFiltersOpen(!filtersOpen)}
              >
                Filtry
              </Button>
              <Button
                variant="text"
                color="inherit"
                size="small"
                startIcon={<CloseIcon fontSize="small" />}
                onClick={onClear}
              >
                Wyczyść filtry
              </Button>
            </Box>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => {
                if (newMode) onViewModeChange(newMode);
              }}
              size="small"
              aria-label="styl widoku zamówień"
              sx={{ display: { xs: "none", sm: "block" } }}
            >
              <ToggleButton value="grid" aria-label="widok kafelków">
                <GridViewIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="list" aria-label="widok listy">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Rozwijana sekcja formularza filtrów */}
        <Collapse in={filtersOpen}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={{ xs: 12, md: 4, sm: 6, lg: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Szukaj"
                    variant="outlined"
                    value={search.text}
                    onChange={(e) => onChange({ text: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4, sm: 6, lg: 2 }}>
                  <Box sx={{ width: "100%", minWidth: 160 }}>
                    <OrderStatusMultiSelect
                      values={search.statuses}
                      onChange={(statuses) => onChange({ statuses })}
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Grid>
                {context?.role === "CUSTOMER" && (
                  <Grid size={{ xs: 12, md: 4, sm: 6, lg: 2 }}>
                    <Box sx={{ width: "100%", minWidth: 160 }}>
                      <ContactPersonSelect
                        customerId={context.customerId}
                        contactPersonEmail={search.contactPersonEmail}
                        onChange={(contactPersonEmail) => onChange({ contactPersonEmail })}
                        size="small"
                      />
                    </Box>
                  </Grid>
                )}
                <Grid size={{ xs: 12, md: 4, sm: 6, lg: 2 }}>
                  <Box sx={{ width: "100%", minWidth: 160 }}>
                    <DatePicker
                      label="Data rozpoczęcia od"
                      size="small"
                      fullWidth
                      value={search.startDate}
                      minDate={dayjs("2016-01-01", ISO_DATE_FORMAT)}
                      maxDate={search.endDate}
                      onChange={(d) => {
                        const next = sanitizeDayjs(d, startOfPreviousMonth());
                        if (next.isAfter(search.endDate, "day")) {
                          onChange({ startDate: next, endDate: next });
                        } else {
                          onChange({ startDate: next });
                        }
                      }}
                    />
                  </Box>
                </Grid>{" "}
                <Grid size={{ xs: 12, md: 4, sm: 6, lg: 2 }}>
                  <Box sx={{ width: "100%", minWidth: 160 }}>
                    <DatePicker
                      label="Data rozpoczęcia do"
                      fullWidth
                      size="small"
                      value={search.endDate}
                      minDate={search.startDate}
                      maxDate={dayjs().add(3, "years")}
                      onChange={(d) => {
                        const next = sanitizeDayjs(d, endOfNextYear());
                        if (next.isBefore(search.startDate, "day")) {
                          onChange({ startDate: next, endDate: next });
                        } else {
                          onChange({ endDate: next });
                        }
                      }}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4, sm: 6, lg: 2 }}>
                  <Box sx={{ width: "100%", minWidth: 160 }}>
                    <TagSelect
                      variant="icon"
                      tags={search.tags}
                      availableTags={tripTagsByCategory}
                      onChange={(tags) => onChange({ tags })}
                    />
                    <ShowHistoricalCheckbox value={showHistorical} onChange={onShowHistoricalChange} />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>
      </Box>
    </>
  );
};

export default CustomerOrdersSearch;
